/**
 * Отображение валюты клуба. Сумма всегда в BYN — настройка меняет только ПОДПИСЬ
 * рядом с числом. Единый источник истины для терминала, бэкофиса и сайта брони.
 *
 *  - 'symbol' — графический знак бел. рубля (Б с горизонтальной чертой, SVG).
 *               В plain-text контекстах (Telegram, fallback) → латинское «Br».
 *  - 'code'   — ISO-код «BYN» (по умолчанию, поведение до введения настройки).
 *  - 'short'  — «бел.руб».
 */
export type CurrencyDisplay = 'symbol' | 'code' | 'short'

/** Значение по умолчанию — сохраняет прежнее поведение (ISO-код BYN). */
export const DEFAULT_CURRENCY_DISPLAY: CurrencyDisplay = 'code'

/** Варианты для селектора в настройках (значение + человекочитаемая подпись). */
export const CURRENCY_DISPLAYS: { value: CurrencyDisplay; label: string }[] = [
  { value: 'symbol', label: 'Знак (Br)' },
  { value: 'code', label: 'BYN' },
  { value: 'short', label: 'бел.руб' }
]

/** Узкая проверка типа для значений, пришедших из БД/облака. */
export function isCurrencyDisplay(v: unknown): v is CurrencyDisplay {
  return v === 'symbol' || v === 'code' || v === 'short'
}

/** Нормализует произвольное значение к CurrencyDisplay (битое → дефолт). */
export function toCurrencyDisplay(v: unknown): CurrencyDisplay {
  return isCurrencyDisplay(v) ? v : DEFAULT_CURRENCY_DISPLAY
}

/**
 * Текстовая подпись валюты — для мест, где НЕЛЬЗЯ отрисовать SVG (Telegram,
 * заголовки/плейсхолдеры, любой plain-text). Знак деградирует до «Br».
 */
export function currencyText(display: CurrencyDisplay): string {
  switch (display) {
    case 'short':
      return 'бел.руб'
    case 'symbol':
      return 'Br'
    case 'code':
    default:
      return 'BYN'
  }
}

/**
 * Inline-SVG знака бел. рубля (Б с горизонтальной чертой). Размер задан в `em`,
 * заливка — `currentColor`, поэтому знак наследует кегль и цвет окружающего
 * текста. Используется и в React-интерфейсах (через dangerouslySetInnerHTML),
 * и в HTML-чеке термопринтера (подстановка в шаблон).
 */
export const CURRENCY_SIGN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="0.78em" height="0.78em" fill="currentColor" style="display:inline-block;vertical-align:-0.04em" role="img" aria-label="бел. руб.">' +
  '<text x="52" y="50" font-family="Arial, Helvetica, sans-serif" font-size="104" font-weight="700" text-anchor="middle" dominant-baseline="central">Б</text>' +
  '<rect x="6" y="44" width="74" height="11" rx="4"/>' +
  '</svg>'

/**
 * HTML-фрагмент подписи валюты для шаблонов (чек принтера): знак → SVG,
 * остальное → текст. Для React используйте компонент `<Cur/>` в каждом приложении.
 */
export function currencyHtml(display: CurrencyDisplay): string {
  return display === 'symbol' ? CURRENCY_SIGN_SVG : currencyText(display)
}
